import React, { useState, useEffect, useCallback } from 'react';
import Pagination from './Pagination';
import './ProxyTest.css';

const API_BASE = '/api';

function ProxyTest() {
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addName, setAddName] = useState('');
  const [addCurl, setAddCurl] = useState('');
  const [addSaving, setAddSaving] = useState(false);

  // Edit
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCurl, setEditCurl] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Collapsible cards
  const [collapsedTest, setCollapsedTest] = useState(true);
  const [collapsedList, setCollapsedList] = useState(true);

  // Test
  const [testingId, setTestingId] = useState(null);
  const [targetUrl, setTargetUrl] = useState('https://api.ipify.org');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  // Test Logs
  const _now = new Date();
  const today = `${_now.getFullYear()}-${String(_now.getMonth()+1).padStart(2,'0')}-${String(_now.getDate()).padStart(2,'0')}`;
  const [testLogs, setTestLogs] = useState([]);
  const [logPagination, setLogPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [logLoading, setLogLoading] = useState(false);
  const [logFilterProxyId, setLogFilterProxyId] = useState('');
  const [logFilterSuccess, setLogFilterSuccess] = useState('');
  const monthStart = `${_now.getFullYear()}-${String(_now.getMonth()+1).padStart(2,'0')}-01`;
  const [logFilterStartDate, setLogFilterStartDate] = useState(monthStart);
  const [logFilterEndDate, setLogFilterEndDate] = useState(today);
  const [expandedLogId, setExpandedLogId] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchProxies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/proxies`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setProxies(data.data || []);
    } catch (err) {
      setMessage({ type: 'error', text: 'Proxy listesi alınamadı' });
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchProxies(); }, [fetchProxies]);

  const fetchTestLogs = useCallback(async (page = 1) => {
    setLogLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', 50);
      if (logFilterProxyId) params.set('proxyId', logFilterProxyId);
      if (logFilterSuccess) params.set('success', logFilterSuccess);
      if (logFilterStartDate) params.set('startDate', logFilterStartDate);
      if (logFilterEndDate) params.set('endDate', logFilterEndDate);
      const res = await fetch(`${API_BASE}/proxies/test-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setTestLogs(data.data || []);
        setLogPagination(data.pagination);
      }
    } catch (err) {
      console.error('Test logs fetch error:', err);
    }
    setLogLoading(false);
  }, [token, logFilterProxyId, logFilterSuccess, logFilterStartDate, logFilterEndDate]);

  useEffect(() => { fetchTestLogs(); }, [fetchTestLogs]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addCurl.trim()) return;
    setAddSaving(true);
    try {
      const res = await fetch(`${API_BASE}/proxies`, {
        method: 'POST', headers,
        body: JSON.stringify({ name: addName, curlProxy: addCurl }),
      });
      const data = await res.json();
      if (data.success) {
        setProxies(prev => [data.data, ...prev]);
        setAddName(''); setAddCurl(''); setShowAddForm(false);
        setMessage({ type: 'success', text: 'Proxy eklendi ✅' });
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    setAddSaving(false);
  };

  const handleEdit = async (id) => {
    setEditSaving(true);
    try {
      const res = await fetch(`${API_BASE}/proxies/${id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ name: editName, curlProxy: editCurl }),
      });
      const data = await res.json();
      if (data.success) {
        setProxies(prev => prev.map(p => p._id === id ? data.data : p));
        setEditingId(null);
        setMessage({ type: 'success', text: 'Proxy güncellendi ✅' });
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    setEditSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu proxy silinsin mi?')) return;
    try {
      const res = await fetch(`${API_BASE}/proxies/${id}`, {
        method: 'DELETE', headers,
      });
      const data = await res.json();
      if (data.success) {
        setProxies(prev => prev.filter(p => p._id !== id));
        setMessage({ type: 'success', text: 'Proxy silindi' });
        if (testingId === id) { setTestingId(null); setTestResult(null); }
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleTest = async (id) => {
    setTestLoading(true);
    setTestResult(null);
    setTestingId(id);
    const startTime = Date.now();
    try {
      const res = await fetch(`${API_BASE}/proxies/${id}/test`, {
        method: 'POST', headers,
        body: JSON.stringify({ targetUrl }),
      });
      const data = await res.json();
      const totalElapsed = Date.now() - startTime;
      setTestResult({ ...data, totalElapsed });
      if (data.success) {
        fetchProxies();
      }
      fetchTestLogs();
    } catch (err) {
      setTestResult({ success: false, message: err.message });
    }
    setTestLoading(false);
  };

  const startEdit = (proxy) => {
    setEditingId(proxy._id);
    setEditName(proxy.name || '');
    setEditCurl(proxy.curlProxy || '');
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' }) : '-';

  return (
    <div className="proxy-page">
      <div className="proxy-header">
        <h2>🌐 Proxy Yönetimi</h2>
        <button className="proxy-add-btn" onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? '✕ İptal' : '+ Proxy Ekle'}
        </button>
      </div>

      {message.text && (
        <div className={`proxy-alert proxy-alert-${message.type}`}>
          {message.text}
          <button className="proxy-alert-close" onClick={() => setMessage({ type: '', text: '' })}>×</button>
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <div className="proxy-card">
          <h3>Yeni Proxy Ekle</h3>
          <form onSubmit={handleAdd} className="proxy-form">
            <div className="proxy-form-row">
              <label>İsim (opsiyonel)</label>
              <input
                type="text" value={addName} onChange={e => setAddName(e.target.value)}
                placeholder="Örn: Ana Proxy"
              />
            </div>
            <div className="proxy-form-row">
              <label>Curl Proxy String</label>
              <input
                type="text" value={addCurl} onChange={e => setAddCurl(e.target.value)}
                placeholder='curl -x https://proxy:port -U "user:pass"'
                required
              />
            </div>
            <div className="proxy-form-actions">
              <button type="submit" className="proxy-save-btn" disabled={addSaving}>
                {addSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="proxy-columns">
      <div className="proxy-col-left">
      {/* Proxy List */}
      <div className="proxy-card">
        <h3 className="proxy-card-toggle" onClick={() => setCollapsedList(!collapsedList)}>
          <span>{collapsedList ? '▶' : '▼'} 📋 Proxy Listesi {!loading && `(${proxies.length})`}</span>
        </h3>
        {!collapsedList && (loading ? (
          <div className="proxy-loading"><div className="spinner" /> Yükleniyor...</div>
        ) : proxies.length === 0 ? (
          <p className="proxy-empty">Henüz proxy eklenmemiş.</p>
        ) : (
          <div className="proxy-list">
            {proxies.map(proxy => (
              <div key={proxy._id} className="proxy-item">
                {editingId === proxy._id ? (
                  <div className="proxy-edit-form">
                    <input
                      type="text" value={editName} onChange={e => setEditName(e.target.value)}
                      placeholder="İsim" className="proxy-edit-input"
                    />
                    <input
                      type="text" value={editCurl} onChange={e => setEditCurl(e.target.value)}
                      placeholder="Curl proxy string" className="proxy-edit-input"
                    />
                    <div className="proxy-edit-actions">
                      <button className="proxy-save-btn" onClick={() => handleEdit(proxy._id)} disabled={editSaving}>
                        {editSaving ? 'Kaydediliyor...' : 'Kaydet'}
                      </button>
                      <button className="proxy-cancel-btn" onClick={() => setEditingId(null)}>İptal</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="proxy-item-info">
                      <div className="proxy-item-name">{proxy.name || 'İsimsiz Proxy'}</div>
                      <div className="proxy-item-url">{proxy.proxyUrl || '-'}</div>
                      <div className="proxy-item-curl">{proxy.curlProxy}</div>
                      <div className="proxy-item-meta">
                        {proxy.lastTestedAt && (
                          <span className={`proxy-last-test ${proxy.lastTestResult?.startsWith('HATA') ? 'test-fail' : 'test-pass'}`}>
                            Son test: {formatDate(proxy.lastTestedAt)} — {proxy.lastTestResult || '-'}
                            {proxy.lastTestIp ? ` (${proxy.lastTestIp})` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="proxy-item-actions">
                      <button className="proxy-btn-edit" onClick={() => startEdit(proxy)} title="Düzenle">✏️</button>
                      <button className="proxy-btn-delete" onClick={() => handleDelete(proxy._id)} title="Sil">🗑️</button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
      </div>

      <div className="proxy-col-right">
      {/* Test Section */}
      <div className="proxy-card">
        <h3 className="proxy-card-toggle" onClick={() => setCollapsedTest(!collapsedTest)}>
          <span>{collapsedTest ? '▶' : '▼'} 🧪 Proxy Test</span>
        </h3>
        {!collapsedTest && <>
        <div className="proxy-test-row">
          <select
            className="proxy-select"
            value={testingId || ''}
            onChange={e => { setTestingId(e.target.value || null); setTestResult(null); }}
          >
            <option value="">Proxy seçin...</option>
            {proxies.map(p => (
              <option key={p._id} value={p._id}>
                {p.name || p.proxyUrl || p.curlProxy.substring(0, 50)}
              </option>
            ))}
          </select>
          <input
            type="text"
            className="proxy-target-input"
            value={targetUrl}
            onChange={e => setTargetUrl(e.target.value)}
            placeholder="https://api.ipify.org"
          />
          <button
            className="proxy-test-btn"
            onClick={() => testingId && handleTest(testingId)}
            disabled={!testingId || testLoading}
          >
            {testLoading ? '⏳ Test...' : '▶ Test Et'}
          </button>
        </div>

        {testResult && (
          <div className={`proxy-test-result ${testResult.success ? 'test-success' : 'test-error'}`}>
            <div className="test-result-header">
              <span className={`test-status-badge ${testResult.success ? 'badge-ok' : 'badge-fail'}`}>
                {testResult.success ? '✅ Başarılı' : '❌ Hata'}
              </span>
              {testResult.totalElapsed && (
                <span className="test-elapsed">{testResult.totalElapsed}ms</span>
              )}
            </div>
            {testResult.success && testResult.data && (
              <div className="test-result-body">
                <div className="test-detail-row">
                  <span className="test-label">Status:</span>
                  <span>{testResult.data.status}</span>
                </div>
                {testResult.data.ip && (
                  <div className="test-detail-row">
                    <span className="test-label">IP:</span>
                    <span className="test-ip">{testResult.data.ip}</span>
                  </div>
                )}
                {testResult.data.elapsed && (
                  <div className="test-detail-row">
                    <span className="test-label">Proxy Süresi:</span>
                    <span>{testResult.data.elapsed}ms</span>
                  </div>
                )}
                <div className="test-detail-row">
                  <span className="test-label">Response:</span>
                </div>
                <pre className="test-response-body">
                  {typeof testResult.data.body === 'string'
                    ? testResult.data.body.substring(0, 2000)
                    : JSON.stringify(testResult.data.body, null, 2).substring(0, 2000)}
                </pre>
              </div>
            )}
            {!testResult.success && (
              <div className="test-result-body">
                <div className="test-detail-row">
                  <span className="test-label">Hata:</span>
                  <span>{testResult.message}</span>
                </div>
              </div>
            )}
          </div>
        )}
        </>}
      </div>
      </div>
      </div>

      {/* Test Logs */}
      <div className="proxy-card">
        <h3>📜 Test Geçmişi {!logLoading && `(${logPagination.total})`}</h3>

        {/* Filters */}
        <div className="proxy-log-filters">
          <select className="proxy-log-filter-select" value={logFilterProxyId} onChange={e => setLogFilterProxyId(e.target.value)}>
            <option value="">Tüm Proxyler</option>
            {proxies.map(p => (
              <option key={p._id} value={p._id}>{p.name || p.proxyUrl || 'İsimsiz'}</option>
            ))}
          </select>
          <select className="proxy-log-filter-select" value={logFilterSuccess} onChange={e => setLogFilterSuccess(e.target.value)}>
            <option value="">Tüm Sonuçlar</option>
            <option value="true">✅ Başarılı</option>
            <option value="false">❌ Hata</option>
          </select>
          <input type="date" className="proxy-log-filter-date" value={logFilterStartDate} onChange={e => setLogFilterStartDate(e.target.value)} />
          <input type="date" className="proxy-log-filter-date" value={logFilterEndDate} onChange={e => setLogFilterEndDate(e.target.value)} />
        </div>

        <Pagination page={logPagination.page} totalPages={logPagination.totalPages || 1} total={logPagination.total} onPageChange={(p) => fetchTestLogs(p)} />

        {logLoading ? (
          <div className="proxy-loading"><div className="spinner" /> Yükleniyor...</div>
        ) : testLogs.length === 0 ? (
          <p className="proxy-empty">Henüz test yapılmamış.</p>
        ) : (
          <div className="proxy-log-list">
            {testLogs.map(log => (
              <div key={log._id} className={`proxy-log-item ${log.success ? 'log-ok' : 'log-fail'}`} onClick={() => setExpandedLogId(expandedLogId === log._id ? null : log._id)}>
                <div className="proxy-log-row">
                  <span className={`proxy-log-badge ${log.success ? 'badge-ok' : 'badge-fail'}`}>
                    {log.success ? '✅' : '❌'}
                  </span>
                  <span className="proxy-log-name">{log.proxyName || 'İsimsiz'}</span>
                  <span className="proxy-log-url">{log.targetUrl}</span>
                  {log.ip && <span className="proxy-log-ip">{log.ip}</span>}
                  <span className="proxy-log-elapsed">{log.totalElapsed || log.elapsed || '-'}ms</span>
                  <span className="proxy-log-date">{formatDate(log.createdAt)}</span>
                </div>
                {expandedLogId === log._id && (
                  <div className="proxy-log-detail">
                    {log.success ? (
                      <>
                        <div className="test-detail-row"><span className="test-label">HTTP Status:</span><span>{log.httpCode}</span></div>
                        {log.ip && <div className="test-detail-row"><span className="test-label">IP:</span><span className="test-ip">{log.ip}</span></div>}
                        <div className="test-detail-row"><span className="test-label">Curl Süresi:</span><span>{log.elapsed}ms</span></div>
                        <div className="test-detail-row"><span className="test-label">Toplam Süre:</span><span>{log.totalElapsed}ms</span></div>
                        {log.responseBody && (
                          <pre className="test-response-body">{log.responseBody.substring(0, 2000)}</pre>
                        )}
                      </>
                    ) : (
                      <div className="test-detail-row"><span className="test-label">Hata:</span><span>{log.error}</span></div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <Pagination page={logPagination.page} totalPages={logPagination.totalPages || 1} total={logPagination.total} onPageChange={(p) => fetchTestLogs(p)} />
      </div>
    </div>
  );
}

export default ProxyTest;
